/**
 * Per-tenant брендинг вкладки браузера (#43).
 *
 * Артём: на `oblaka.lovarthotels.ru` во вкладке был синий ДЕФОЛТНЫЙ favicon
 * (Vite/React заглушка `H` на синем) и общий заголовок «Lovart Hotels».
 * Нужно: иконка = логотип отеля, title = название отеля («Облака»).
 *
 * Решение: на старте приложения резолвим slug по поддомену и тянем публичную
 * карточку `GET /api/v1/public/{slug}/hotel-info` (без JWT). Из неё берём
 * `display_name` → `document.title` и `logo_url` → `<link rel="icon">`.
 *
 * Деградация:
 *   - нет slug / запрос упал → оставляем нейтральный LovArt-фоллбэк из
 *     index.html (favicon.svg = LA-марка, НЕ синий Vite) и title как есть;
 *   - есть display_name, но нет logo_url → ставим корректный per-tenant title,
 *     favicon остаётся нейтральным LovArt (а не синим Vite).
 *
 * Делает это императивно (DOM), вне React, чтобы вкладка обновилась ещё до
 * первого рендера и работала на ВСЕХ роутах (логин, витрина, кабинеты роли) —
 * один поддомен на отель (feedback_one_subdomain_per_hotel_role_routing).
 */
import { resolveTenantSlug } from './tenant-slug.js';

interface HotelInfoBrand {
  display_name?: string;
  logo_url?: string;
}

/** Базовый путь API — тот же префикс `/api`, что и у customFetch. */
const API_BASE = '/api/v1';

/** Заменяет (или создаёт) `<link rel="icon">` на указанный href. */
function setFavicon(href: string): void {
  if (typeof document === 'undefined') return;
  const head = document.head;
  if (!head) return;
  // Удаляем все существующие иконки (включая дефолтный favicon.svg),
  // чтобы браузер не показывал старую.
  head.querySelectorAll('link[rel~="icon"]').forEach((el) => el.remove());
  const link = document.createElement('link');
  link.rel = 'icon';
  // Тип по расширению — png/svg/ico; для прочего не задаём (браузер определит).
  if (/\.svg(\?|$)/i.test(href)) link.type = 'image/svg+xml';
  else if (/\.png(\?|$)/i.test(href)) link.type = 'image/png';
  link.href = href;
  head.appendChild(link);
}

/** Ставит заголовок вкладки = название отеля. */
function setTitle(name: string): void {
  if (typeof document === 'undefined') return;
  const trimmed = name.trim();
  if (trimmed.length > 0) document.title = trimmed;
}

/**
 * Применяет per-tenant брендинг вкладки (title + favicon).
 *
 * Никогда не бросает — любые ошибки сети/парсинга проглатываются, остаётся
 * нейтральный LovArt-фоллбэк из index.html.
 */
export async function applyTenantBranding(): Promise<void> {
  try {
    const slug = resolveTenantSlug();
    if (!slug) return;

    const resp = await fetch(`${API_BASE}/public/${encodeURIComponent(slug)}/hotel-info`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'omit',
    });
    if (!resp.ok) return;

    const info = (await resp.json()) as HotelInfoBrand;

    if (info.display_name) setTitle(info.display_name);
    if (info.logo_url && info.logo_url.trim().length > 0) setFavicon(info.logo_url.trim());
  } catch {
    /* фоллбэк уже в index.html (нейтральный LovArt favicon + title) */
  }
}
